import type { JSONContent } from '@tiptap/react';
import type { Block } from '../types';
import { normalizeBlocks } from '../normalizeBlocks';
import { htmlToInline } from './richtextHtml';
import { ADVANCED_BLOCK_NODES } from './advancedBlockMap';

/**
 * Block[] → TipTap document JSON. Each block becomes one top-level node
 * (ported from Scilora `serialization/blocks-to-tiptap.ts`).
 */
export function blocksToTiptap(blocks: Block[]): JSONContent {
    if (blocks.length === 0) {
        return {
            type: 'doc',
            content: [{ type: 'richtextBlock', attrs: { blockId: null, blockMeta: null, html: '' } }],
        };
    }

    const content = normalizeBlocks(blocks).map(blockToNode);
    // #526: always end with a text block so there is somewhere to click/type
    // after an atom block (image, quiz …). tiptapToBlocks drops it again while
    // it stays empty, so opening a lesson does not make it dirty.
    if (content[content.length - 1].type !== 'richtextBlock') {
        content.push({ type: 'richtextBlock', attrs: { blockId: null, blockMeta: null, html: '' } });
    }

    return { type: 'doc', content };
}

function blockToNode(block: Block): JSONContent {
    const baseAttrs = { blockId: block.id, blockMeta: block.meta ?? null };

    switch (block.type) {
        case 'richtext':
            return richtextToNode(block, baseAttrs);
        case 'heading':
            return headingToNode(block, baseAttrs);
        case 'image':
            return imageToNode(block, baseAttrs);
        case 'divider':
            return { type: 'dividerBlock', attrs: baseAttrs };
        case 'callout':
            return calloutToNode(block, baseAttrs);
        case 'quote':
            return quoteToNode(block, baseAttrs);
        case 'code':
            return codeToNode(block, baseAttrs);
        case 'video':
            return spreadToNode('videoBlock', block, baseAttrs);
        case 'audio':
            return spreadToNode('audioBlock', block, baseAttrs);
        case 'file':
            return spreadToNode('fileBlock', block, baseAttrs);
        case 'embed':
            return spreadToNode('embedBlock', block, baseAttrs);
        case 'columns':
            return spreadToNode('columnsBlock', block, baseAttrs);
        case 'accordion':
            return spreadToNode('accordionBlock', block, baseAttrs);
        case 'tabs':
            return spreadToNode('tabsBlock', block, baseAttrs);
        case 'table':
            return tableToNode(block, baseAttrs);
        case 'inline_quiz':
            return spreadToNode('inlineQuizBlock', block, baseAttrs);
        case 'fill_blank':
            return spreadToNode('fillBlankBlock', block, baseAttrs);
        case 'matching':
            return spreadToNode('matchingBlock', block, baseAttrs);
        case 'ordering':
            return spreadToNode('orderingBlock', block, baseAttrs);
        case 'flashcard':
            return spreadToNode('flashcardBlock', block, baseAttrs);
        case 'discussion':
            return spreadToNode('discussionBlock', block, baseAttrs);
        case 'poll':
            return spreadToNode('pollBlock', block, baseAttrs);
        case 'scorm_embed':
            return spreadToNode('scormEmbedBlock', block, baseAttrs);
        default: {
            const advancedNode = ADVANCED_BLOCK_NODES[block.type];
            if (advancedNode) {
                return spreadToNode(advancedNode, block, baseAttrs);
            }
            return {
                type: 'unknownBlock',
                attrs: { ...baseAttrs, originalType: block.type, data: block.data },
            };
        }
    }
}

function richtextToNode(block: Block, baseAttrs: Record<string, unknown>): JSONContent {
    const html = (block.data as { html?: string }).html ?? '';
    const content = htmlToInline(html);
    return {
        type: 'richtextBlock',
        attrs: { ...baseAttrs, html },
        ...(content.length > 0 ? { content } : {}),
    };
}

function headingToNode(block: Block, baseAttrs: Record<string, unknown>): JSONContent {
    const data = block.data as { text?: string; level?: number };
    return {
        type: 'headingBlock',
        attrs: { ...baseAttrs, level: data.level ?? 2 },
        content: data.text ? [{ type: 'text', text: data.text }] : [],
    };
}

function imageToNode(block: Block, baseAttrs: Record<string, unknown>): JSONContent {
    const data = block.data as { src?: string; alt?: string; caption?: string; alignment?: string; mediaFileUuid?: string };
    return {
        type: 'imageBlock',
        attrs: {
            ...baseAttrs,
            src: data.src ?? '',
            alt: data.alt ?? '',
            caption: data.caption ?? '',
            alignment: data.alignment ?? 'center',
            mediaFileUuid: data.mediaFileUuid ?? '',
        },
    };
}

function calloutToNode(block: Block, baseAttrs: Record<string, unknown>): JSONContent {
    const data = block.data as { html?: string; variant?: string; icon?: string };
    return {
        type: 'calloutBlock',
        attrs: { ...baseAttrs, variant: data.variant ?? 'info', icon: data.icon ?? '', html: data.html ?? '' },
    };
}

function quoteToNode(block: Block, baseAttrs: Record<string, unknown>): JSONContent {
    const data = block.data as { text?: string; attribution?: string };
    return {
        type: 'quoteBlock',
        attrs: { ...baseAttrs, attribution: data.attribution ?? '' },
        content: data.text ? [{ type: 'text', text: data.text }] : [],
    };
}

function codeToNode(block: Block, baseAttrs: Record<string, unknown>): JSONContent {
    const data = block.data as { code?: string; language?: string; showLineNumbers?: boolean; caption?: string };
    return {
        type: 'codeBlockNode',
        attrs: {
            ...baseAttrs,
            language: data.language ?? '',
            showLineNumbers: data.showLineNumbers ?? false,
            caption: data.caption ?? '',
        },
        content: data.code ? [{ type: 'text', text: data.code }] : [],
    };
}

function tableToNode(block: Block, baseAttrs: Record<string, unknown>): JSONContent {
    const data = block.data as { rows?: string[][]; hasHeader?: boolean; caption?: string };
    return {
        type: 'tableBlock',
        attrs: { ...baseAttrs, rows: data.rows ?? [['', '']], hasHeader: data.hasHeader ?? true, caption: data.caption ?? '' },
    };
}

/** Atom blocks whose node attrs are exactly the block data. */
function spreadToNode(nodeType: string, block: Block, baseAttrs: Record<string, unknown>): JSONContent {
    return { type: nodeType, attrs: { ...baseAttrs, ...block.data } };
}
